'use client';

import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  deriveInstitutionSetupProgress,
  formatInstitutionPlanBadge,
  type InstitutionSetupProgress,
  type InstitutionWorkspaceRole,
} from '@/lib/institution-workspace';
import { EMPTY_INSTITUTION_METRICS, type InstitutionMetrics } from '@/lib/institution-metrics';
import { useInstitutionWorkspaceContext } from '@/contexts/InstitutionWorkspaceContext';
import type { InstitutionEffectiveRole } from '@/lib/institution-roles';

export type InstitutionWorkspaceOption = {
  id: string;
  name: string;
  institutionId: string;
};

export type InstitutionOverviewAvailability = 'loading' | 'available' | 'unavailable';

const ROLE_DISPLAY: Record<InstitutionEffectiveRole, InstitutionWorkspaceRole> = {
  owner: 'Owner',
  admin: 'Admin',
  verifier: 'Verifier',
  archivist: 'Archivist',
  viewer: 'Member',
  member: 'Member',
};

/** Reads the tenant already resolved by InstitutionWorkspaceProvider (owner or delegated member)
 * and layers on setup-progress and overview-metrics fetching. Never derives institution id from
 * the caller's own user id — every value here comes from the server-resolved workspace. */
export function useInstitutionWorkspace() {
  const { profile, isPresentationDemo, user } = useAuth();
  const workspace = useInstitutionWorkspaceContext();
  const [setupProgress, setSetupProgress] = useState<InstitutionSetupProgress>(() =>
    deriveInstitutionSetupProgress(null, EMPTY_INSTITUTION_METRICS, false)
  );
  const [pendingInbox, setPendingInbox] = useState(0);
  const [overviewAvailability, setOverviewAvailability] =
    useState<InstitutionOverviewAvailability>('loading');
  const [overviewError, setOverviewError] = useState<string | null>(null);

  const isReady = workspace.status === 'ready';
  const institutionId = isReady ? workspace.institutionId : '';
  const institutionName = isReady ? workspace.institutionName : '';
  const institutionType = isReady ? workspace.institutionType : null;
  const publicHandle = isReady ? workspace.publicHandle : null;
  const publicProfilePublished = isReady ? workspace.publicProfilePublished : false;
  const logoUrl = isReady ? workspace.logoUrl : null;
  const role: InstitutionEffectiveRole = isReady ? workspace.role : 'member';
  const isOwner = isReady ? workspace.isOwner : false;
  const memberRole = ROLE_DISPLAY[role];
  const planBadge = formatInstitutionPlanBadge(profile?.subscription_tier, {
    isPresentationDemo,
    email: user?.email,
  });

  const workspaceOptions: InstitutionWorkspaceOption[] =
    workspace.status === 'multiple'
      ? workspace.candidates.map((c) => ({
          id: c.institutionId,
          name: c.institutionName,
          institutionId: c.institutionId,
        }))
      : isReady
        ? workspace.workspaces.map((c) => ({
            id: c.institutionId,
            name: c.institutionName,
            institutionId: c.institutionId,
          }))
        : [];

  const loadOverview = useCallback(
    async (signal?: AbortSignal) => {
      if (!isReady) return;
      setOverviewAvailability('loading');
      setOverviewError(null);
      const res = await fetch(
        `/api/institution/overview?institution_id=${encodeURIComponent(institutionId)}`,
        { cache: 'no-store', signal }
      ).catch(() => null);
      if (signal?.aborted) return;
      const body = res
        ? ((await res.json().catch(() => null)) as {
            available?: boolean;
            metrics?: InstitutionMetrics | null;
            error?: string;
          } | null)
        : null;
      if (signal?.aborted) return;

      if (!res?.ok || body?.available !== true || !body.metrics) {
        setOverviewAvailability('unavailable');
        setOverviewError(
          body?.error || 'Institution metrics are temporarily unavailable. Please retry.'
        );
        return;
      }

      const metrics = body.metrics;
      setPendingInbox(metrics.pendingSubmissions);
      setSetupProgress(
        deriveInstitutionSetupProgress(
          isOwner
            ? {
                avatar_url: profile?.avatar_url,
                institution_name: profile?.institution_name,
                full_name: profile?.full_name,
                bio: profile?.bio,
                public_handle: (profile as { public_handle?: string | null } | null)?.public_handle,
              }
            : { avatar_url: logoUrl, institution_name: institutionName },
          metrics,
          Boolean(
            (
              (profile as { settings_json?: Record<string, unknown> | null } | null)
                ?.settings_json as Record<string, unknown> | undefined
            )?.institution_intake_enabled
          )
        )
      );
      setOverviewAvailability('available');
    },
    [isReady, institutionId, institutionName, isOwner, logoUrl, profile]
  );

  useEffect(() => {
    const controller = new AbortController();
    void loadOverview(controller.signal);
    return () => controller.abort();
  }, [loadOverview]);

  return {
    loading: workspace.status === 'loading',
    institutionName,
    institutionId,
    institutionType,
    publicHandle,
    publicProfilePublished,
    logoUrl,
    planBadge,
    role,
    isOwner,
    memberRole,
    workspaces: workspaceOptions,
    selectWorkspace: workspace.selectWorkspace,
    setupProgress,
    pendingInbox,
    overviewAvailability,
    overviewError,
    needsSetup: isOwner && !institutionName && !isPresentationDemo,
    workspaceStatus: workspace.status,
    workspaceCandidates: workspace.status === 'multiple' ? workspace.candidates : [],
    refresh: () => {
      workspace.refresh();
      void loadOverview();
    },
  };
}
